/**
 * Admin V3 — Devis (quotations) — list page / operator work queue
 *
 * Source: GET /admin/devis → { devis, count, total, limit, offset }  (ADMIN_PARTNER_LIST).
 * F-1 work queue: status filter + free-text search + offset/limit pagination so
 * operators reliably discover pending work (e.g. ACCEPTED devis) and nothing
 * disappears beyond the list limit. Permission-gated "Créer un devis"
 * (ADMIN_PARTNER_UPDATE) opens an inline create form. Row → detail. Commercial
 * layer, not Financial Core.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, ExternalLink, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard, SmallPill, EmptyState, currency, devisStatusMeta } from '../../shared/visualTokens';
import { WorkQueueToolbar, WorkQueuePager } from '../../shared/WorkQueueControls';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import usePermissions from '../../shared/hooks/usePermissions';
import { t, DEFAULT_LOCALE } from '../../i18n';
import DevisCreateForm from './DevisCreateForm';

const PERM_UPDATE = 'admin:partner:update';
const DEFAULT_LIMIT = 50;

const customerLabel = (c) => (c && (c.company || c.name)) || '—';

const DevisListPage = () => {
  const locale = DEFAULT_LOCALE;
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can(PERM_UPDATE);
  const [createOpen, setCreateOpen] = React.useState(false);

  // Work-queue state (F-1): status filter, debounced search, pagination offset.
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [offset, setOffset] = React.useState(0);

  // Debounce free-text search; any new search resets to the first page.
  React.useEffect(() => {
    const id = setTimeout(() => { setSearch(searchInput.trim()); setOffset(0); }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const params = {
    limit: DEFAULT_LIMIT,
    offset,
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(search ? { search } : {}),
  };

  const { data, loading, error, refetch } = useAdminQuery('/admin/devis', params);
  const items = Array.isArray(data?.devis) ? data.devis : [];
  const total = Number.isFinite(data?.total) ? data.total : (data?.count ?? items.length);
  const showSkeleton = loading && !data;

  const onStatus = (v) => { setStatusFilter(v); setOffset(0); };

  return (
    <SectionCard
      title={t('devis.title', locale)} icon={FileText} iconClass="bg-indigo-100 text-indigo-600"
      action={canCreate ? (
        <Button variant="default" size="sm" onClick={() => setCreateOpen((v) => !v)} data-testid="adminv3-devis-create-toggle">
          <Plus className="h-3.5 w-3.5 mr-1.5" />{t('devis.action.create', locale)}
        </Button>
      ) : null}
    >
      <div className="space-y-4">
        {createOpen && canCreate && (
          <DevisCreateForm
            onCreated={(id) => { setCreateOpen(false); refetch(); if (id) navigate(`/admin/billing/devis/${id}`); }}
            onCancel={() => setCreateOpen(false)}
          />
        )}

        <WorkQueueToolbar
          statusValue={statusFilter} onStatusChange={onStatus} statusMeta={devisStatusMeta}
          search={searchInput} onSearchChange={setSearchInput}
          testidPrefix="adminv3-devis" locale={locale}
        />

        {data && (
          <div className="text-[12px] text-slate-500" data-testid="adminv3-devis-summary">
            {t('devis.summary', locale).replace('{n}', String(total))}
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3" data-testid="adminv3-devis-error">
            <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-rose-700">{t('devis.error.title', locale)}</p>
              <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={refetch} className="border-rose-200 bg-white text-rose-700 hover:bg-rose-50">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />{t('devis.error.retry', locale)}
            </Button>
          </div>
        )}

        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {[
                  t('devis.col.number', locale), t('devis.col.customer', locale), t('devis.col.total', locale),
                  t('devis.col.version', locale), t('devis.col.status', locale), t('devis.col.actions', locale),
                ].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {showSkeleton ? (
                Array(4).fill(0).map((_, i) => (
                  <tr key={`sk-${i}`} className="border-b border-slate-100">
                    {Array(6).fill(0).map((_, j) => (<td key={j} className="px-4 py-3.5"><div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" /></td>))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8">
                  <EmptyState icon={FileText} title={t('devis.empty.title', locale)} desc={t('devis.empty.desc', locale)} />
                </td></tr>
              ) : (
                items.map((d) => {
                  const key = d.id || d.devis_number;
                  return (
                    <tr key={key} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                      <td className="px-4 py-3.5">
                        <span className="inline-block rounded-lg bg-indigo-50 px-2 py-1 font-mono text-[12px] font-bold text-indigo-700">
                          {d.devis_number || t('devis.draftBadge', locale)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-slate-900 font-medium text-[13px]">{customerLabel(d.customer)}</td>
                      <td className="px-4 py-3.5 font-bold text-slate-900 whitespace-nowrap">{currency(d.total_ttc_estimated ?? 0)}</td>
                      <td className="px-4 py-3.5 text-slate-500 text-[12px]">v{d.version ?? 1}</td>
                      <td className="px-4 py-3.5"><SmallPill meta={devisStatusMeta[d.status] || devisStatusMeta.draft} /></td>
                      <td className="px-4 py-3.5">
                        <button type="button" onClick={() => navigate(`/admin/billing/devis/${key}`)}
                                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                                title={t('devis.action.open', locale)} data-testid={`adminv3-devis-open-${key}`}>
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <WorkQueuePager
          offset={offset} limit={DEFAULT_LIMIT} total={total} shown={items.length}
          onPrev={() => setOffset((o) => Math.max(0, o - DEFAULT_LIMIT))}
          onNext={() => setOffset((o) => o + DEFAULT_LIMIT)}
          testidPrefix="adminv3-devis" locale={locale}
        />
      </div>
    </SectionCard>
  );
};

export default DevisListPage;
